// TODO: vitest component test (pages are covered by Playwright e2e)
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'
import { Plus, X, GripVertical, RotateCw, Bell, ChevronRight, UserCog, QrCode, Download } from 'lucide-react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Input, Select, Alert, Skeleton, Badge, FileUpload, FileInput, ConfirmDialog, Toggle, Checkbox } from '@/components/ui'
import { Tabs } from '@/components/ui/Tabs'
import { Textarea } from '@/components/ui/Textarea'
import { isAxiosError } from 'axios'
import { api } from '@/lib/api'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useAccountSettings, useUpdateAccountSettings } from '@/hooks/useAccountSettings'
import { useAccountForm } from '@/hooks/useAccountForm'
import { usePaymentSettings } from '@/hooks/usePaymentSettings'
import { useLiffSettings } from '@/hooks/useLiffSettings'
import { useCarrierSettings } from '@/hooks/useCarrierSettings'
import { useCategorySettings } from '@/hooks/useCategorySettings'
import { useExpenseCategorySettings } from '@/hooks/useExpenseCategorySettings'
import { useSourceSettings } from '@/hooks/useSourceSettings'
import { useAuth } from '@/hooks/useAuth'
import { useSupportSession } from '@/hooks/useSupportSession'
import { useFeatureFlags } from '@/lib/featureFlags'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useUIStore } from '@/stores'
import { accountSettingsKeys } from '@/hooks/useAccountSettings'
import { WinPickupSettingsSection } from '@/components/settings/WinPickupSettingsSection'
import { LineOaSettingsCard, type LineOaSettingsCardHandle } from '@/components/settings/LineOaSettingsCard'
import { AppIntegrationSettingsCard } from '@/components/settings/AppIntegrationSettingsCard'
import { ExpenseCategoryGlSelect } from '@/components/settings/ExpenseCategoryGlSelect'
import { SettingsSaveAlert } from '@/components/settings/SettingsSaveAlert'
import { SettingsSaveBar } from '@/components/settings/SettingsSaveBar'
import { SubscriptionTab } from './subscription/SubscriptionTab'
import { AccountDeletionPanel } from '@/pages/account/AccountDeletionPanel'
import { DataSubjectRequestsCard } from '@/components/settings/DataSubjectRequestsCard'
import { SupportAccessCard } from '@/components/settings/SupportAccessCard'
import { useRequestAccountExport } from '@/hooks/useAccountExport'
import { isIosApp } from '@/lib/platform'
import { useFitOneLine } from '@/hooks/useFitOneLine'
import { liffLoginPreviewVars, LOGIN_TITLE_ALIGNS, LOGIN_PANEL_MARGIN_MAX_PX, LOGIN_PANEL_PADDING_MAX_PX, LOGIN_PANEL_RADIUS_MAX_PX } from '@/lib/liffLoginTheme'
import '@/styles/liff-login.css'

const API_BASE = import.meta.env.VITE_API_URL || ''
function resolveApiUrl(url: string): string {
  return url.startsWith('/api/') ? `${API_BASE}${url}` : url
}

// EP-160: PrivacyTerms::CONTROLLER_FIELD_MAX on the server, mirrored so a
// long value is stopped while typing rather than refused on save.
const CONTROLLER_FIELD_MAX = 200

const timezoneOptions = [
  { value: 'Asia/Bangkok', label: 'Asia/Bangkok (ICT +07:00)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST +09:00)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT +08:00)' },
  { value: 'UTC', label: 'UTC (+00:00)' },
]

const dateFormatOptions = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
]

const calendarOptions = [
  { value: 'gregorian', label: 'Gregorian (CE)' },
  { value: 'buddhist', label: 'Buddhist (BE)' },
]

// B-008: render each language as "<localized> (<native>)", but de-dupe to just
// the native name when they're identical (e.g. in the Thai UI, languages.th is
// already "ไทย", so don't show "ไทย (ไทย)").
const LANGUAGE_NATIVE_NAMES: Record<string, string> = { th: 'ไทย', en: 'English' }
function languageOptions(t: (key: string) => string) {
  return (['th', 'en'] as const).map((code) => {
    const localized = t(`languages.${code}`)
    const native = LANGUAGE_NATIVE_NAMES[code]
    return { value: code, label: localized === native ? native : `${localized} (${native})` }
  })
}

const currencyOptions = [
  { value: 'THB', label: 'THB (฿)' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
]

type SettingsTab = 'account' | 'payments' | 'liff' | 'app_integration' | 'carriers' | 'categories' | 'expense_categories' | 'sources' | 'win_pickup' | 'subscription' | 'support_access' | 'data_requests' | 'delete_account'

const VALID_TABS: SettingsTab[] = ['account', 'payments', 'liff', 'app_integration', 'carriers', 'categories', 'expense_categories', 'sources', 'win_pickup', 'subscription', 'support_access', 'data_requests', 'delete_account']
function isValidTab(value: string | null | undefined): value is SettingsTab {
  return value != null && (VALID_TABS as readonly string[]).includes(value)
}

export function SettingsPage() {
  const { t } = useTranslation('settings')
  const { user } = useAuth()
  const navigate = useNavigate()
  const { isLoading, data: accountSettings } = useAccountSettings()
  const invoiceQrKey = accountSettings?.settings?.invoice_qr_image
  const updateAccountSettings = useUpdateAccountSettings()
  const featureFlags = useFeatureFlags()

  const canEdit = user?.role === 'owner' || user?.role === 'admin'
  // EP-160 S3: in a FlowRent support session the tabs and links the deny list
  // refuses (SupportSession::Blocked) are not offered: the team, the activity
  // log, the mobile app keys, FlowRent help, personal data requests and
  // account deletion.
  const supportSession = useSupportSession().active
  // EP-99: dual-gate — Unleash flag `win_pickup` AND the per-account setting.
  const showWinPickup = featureFlags.winPickup && (accountSettings?.settings?.win_pickup_enabled ?? false)
  // EP-157 (owner, 2026-10-08): the mobile app tab is an admin-managed module,
  // switched on per account from admin-cli; owner and admin only.
  const showAppIntegration = canEdit && !supportSession && (accountSettings?.settings?.app_integration_enabled ?? false)
  const showAccountControls = canEdit && !supportSession
  const [searchParams] = useSearchParams()
  // EP-160 S2: the support access mail and push link to
  // /settings/support-access?request=<id> (SupportAccessMailer::SETTINGS_PATH),
  // which opens the FlowRent help tab.
  const onSupportAccessPath = useLocation().pathname === '/settings/support-access'
  const initialTab = onSupportAccessPath
    ? 'support_access'
    : ((searchParams.get('tab') as SettingsTab | null) ?? 'account')
  const requestParam = Number(searchParams.get('request'))
  const supportRequestId = Number.isInteger(requestParam) && requestParam > 0 ? requestParam : null
  // EP-150 (App Review 2.1b): FlowRent's own billing is kept out of the iOS app.
  const showSubscription = user?.role === 'owner' && !isIosApp()
  const { t: tAccount } = useTranslation('account')
  const deleteTabLabel =
    user?.role === 'owner' ? tAccount('delete.title_account') : tAccount('delete.title_user')
  const resolvedInitialTab: SettingsTab = (() => {
    if (!isValidTab(initialTab)) return 'account'
    // Deep-link guard: expense_categories is hidden from non-editors; fall back to account.
    if (initialTab === 'expense_categories' && !canEdit) return 'account'
    // EP-157: hidden unless the account has the module and the user is owner/admin.
    if (initialTab === 'app_integration' && !showAppIntegration) return 'account'
    // Deep-link guard: win_pickup tab is hidden when module is off.
    if (initialTab === 'win_pickup' && !showWinPickup) return 'account'
    // EP-144 (Q-9): the subscription tab is OWNER-only, narrower than canEdit
    // (which is owner OR admin), so an admin deep-link falls back to account.
    // EP-150: and it is not shown in the iOS app at all.
    if (initialTab === 'subscription' && !showSubscription) return 'account'
    // EP-160: personal data requests are owner and admin only.
    if (initialTab === 'data_requests' && !showAccountControls) return 'account'
    // EP-160 S1: FlowRent help is the owner's, and an admin reads it.
    if (initialTab === 'support_access' && !showAccountControls) return 'account'
    if (initialTab === 'delete_account' && supportSession) return 'account'
    return initialTab
  })()
  const [activeTab, setActiveTab] = useState<SettingsTab>(resolvedInitialTab)
  const [replayConfirmOpen, setReplayConfirmOpen] = useState(false)
  const [brokenQrKey, setBrokenQrKey] = useState<string | null>(null)

  // iOS shows <main>'s background when the page is pulled past either end.
  // The page paints its own grey (the root div below) and <main> is grey on
  // top and white below, so a pull at the top shows grey and a pull at the
  // bottom shows white under the white save bar (owner, 2026-09-30). A layout
  // effect so the first paint already has it.
  const setPagePaintsBackground = useUIStore((s) => s.setPagePaintsBackground)
  useLayoutEffect(() => {
    setPagePaintsBackground(true)
    return () => setPagePaintsBackground(false)
  }, [setPagePaintsBackground])
  const addToast = useNotificationStore((s) => s.addToast)

  // ?focus=tax_id scrolls the matching field into view + focuses it after the
  // settings data has loaded and the input has rendered. Used by the
  // InvoiceDetailPage tax-invoice button when tax_id is missing.
  const focusField = searchParams.get('focus')
  const taxIdInputRef = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    if (focusField === 'tax_id' && activeTab === 'account' && !isLoading && taxIdInputRef.current) {
      const el = taxIdInputRef.current
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el.focus({ preventScroll: true })
    }
  }, [focusField, activeTab, isLoading])

  const accountForm = useAccountForm()
  const paymentSettings = usePaymentSettings()
  const liffSettings = useLiffSettings()
  const previewTitleRef = useFitOneLine(t('liff.login_preview_page.title', { lng: 'th' }))
  const carrierSettings = useCarrierSettings()
  const categorySettings = useCategorySettings()
  const expenseCategorySettings = useExpenseCategorySettings()
  const sourceSettings = useSourceSettings()
  const queryClient = useQueryClient()
  const replayOnboarding = useMutation({
    mutationFn: async () => {
      await updateAccountSettings.mutateAsync({
        settings: {
          onboarding_completed: false,
          onboarding_step: undefined,
        },
      })
      // Also clear all the AI import state fields using the raw PATCH endpoint.
      await api.patch('/api/account_settings', {
        account_settings: {
          settings: {
            onboarding_data_import_id: null,
            onboarding_data_import_cursor: {},
            onboarding_data_import_applied: [],
            onboarding_data_import_resolver: {},
          },
        },
      })
    },
    onSuccess: () => {
      setReplayConfirmOpen(false)
      navigate('/onboarding?step=1')
    },
  })

  const showSuccess = () => {
    addToast({ type: 'success', message: t('messages.settings_saved') })
  }

  const handleSaveAccount = async (e: React.FormEvent) => {
    if (!canEdit) return
    const saved = await accountForm.handleSave(e)
    if (saved) showSuccess()
  }

  const handleSavePayments = async (e: React.FormEvent) => {
    if (!canEdit) return
    const saved = await paymentSettings.handleSave(e)
    if (saved) showSuccess()
  }

  // One save bar for the whole LINE tab: the LINE OA card's keys first, then
  // the theme. Owner 2026-10-08: the card had its own Save button, and the
  // keys typed into it were silently dropped by this bar (or by Enter).
  const lineOaCardRef = useRef<LineOaSettingsCardHandle>(null)
  const [savingLiff, setSavingLiff] = useState(false)
  const handleSaveLiff = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canEdit) return
    setSavingLiff(true)
    try {
      const lineSaved = (await lineOaCardRef.current?.save()) ?? true
      const saved = await liffSettings.handleSave(e)
      if (lineSaved && saved) showSuccess()
    } finally {
      setSavingLiff(false)
    }
  }

  const handleSaveCarriers = async () => {
    if (!canEdit) return
    const saved = await carrierSettings.handleSave()
    if (saved) showSuccess()
  }

  const handleSaveCategories = async () => {
    if (!canEdit) return
    const saved = await categorySettings.handleSave()
    if (saved) showSuccess()
  }

  const handleSaveExpenseCategories = async () => {
    if (!canEdit) return
    const saved = await expenseCategorySettings.handleSave()
    if (saved) showSuccess()
  }

  const handleSaveSources = async () => {
    if (!canEdit) return
    const saved = await sourceSettings.handleSave()
    if (saved) showSuccess()
  }

  const uploadThemeImage = async (file: File): Promise<string> => {
    const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
      filename: file.name, content_type: file.type,
    })
    await fetch(presigned.url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } })
    return `/api/images/${presigned.key}`
  }

  // EP-73 C4 — LINE rich menu (owner-only card in the LIFF tab). EP-156
  // T156-007: the channel access token field moved to the LINE Official
  // Account card, which saves it via useUpdateAccountSettings directly.
  const [richMenuFile, setRichMenuFile] = useState<File | null>(null)
  const [isApplyingRichMenu, setIsApplyingRichMenu] = useState(false)

  const handleApplyRichMenu = async () => {
    if (!richMenuFile) return
    setIsApplyingRichMenu(true)
    try {
      const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
        filename: richMenuFile.name, content_type: richMenuFile.type,
      })
      await fetch(presigned.url, { method: 'PUT', body: richMenuFile, headers: { 'Content-Type': richMenuFile.type } })
      await api.post('/api/line/rich_menu', { image_key: presigned.key })
      addToast({ type: 'success', message: t('rich_menu.apply_success') })
    } catch {
      addToast({ type: 'error', message: t('rich_menu.apply_error') })
    } finally {
      setIsApplyingRichMenu(false)
    }
  }

  // EP-73 — AI feature coach toggle/caps/BYO-key (account tab card).
  const aiCoachEnabled = accountSettings?.settings?.ai_coach_enabled === true
  const [coachMaxFeatures, setCoachMaxFeatures] = useState<string>(
    String(accountSettings?.settings?.ai_coach_max_features ?? 5),
  )
  const patchCoachSettings = async (settings: Record<string, unknown>, successMsg: string) => {
    try {
      await api.patch('/api/account_settings', { account_settings: { settings } })
      queryClient.invalidateQueries({ queryKey: accountSettingsKeys.detail() })
      addToast({ type: 'success', message: successMsg })
    } catch {
      addToast({ type: 'error', message: t('ai_coach.save_error') })
    }
  }

  const toggleCoach = () =>
    patchCoachSettings({ ai_coach_enabled: !aiCoachEnabled }, t('ai_coach.saved'))

  const saveCoachMaxFeatures = () => {
    const n = Number(coachMaxFeatures)
    if (!Number.isInteger(n) || n < 1) return
    patchCoachSettings({ ai_coach_max_features: n }, t('ai_coach.saved'))
  }

  if (isLoading) {
    return (
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Card><Skeleton className="h-64" /></Card>
        </div>
    )
  }

  const { form } = accountForm
  const { register, formState: { errors } } = form

  return (
    <>
      {/* The grey page, painted here rather than by <main> below lg (see
          setPagePaintsBackground above). <main> has no padding there, so this
          carries <main>'s usual padding itself, and min-h-full keeps a short
          tab grey to the bottom of the screen. SettingsSaveBar cancels the
          bottom padding with its own negative margin, so where the bar rests
          at the end of a tab it reaches the screen edge. */}
      <div className="min-h-full space-y-6 bg-base-200 px-4 pt-6 pb-[max(env(safe-area-inset-bottom),1rem)] lg:min-h-0 lg:bg-transparent lg:p-0">
        <Breadcrumbs items={[{ label: t('title') }]} />
        <PageHeader title={t('title')} description={t('description')} />

        {!canEdit && (
          <Alert variant="info">{t('account.read_only_notice')}</Alert>
        )}

        {/* Team quick-link — admin/owner only (P9). Mirrors the Sidebar
            adminNavItems order (team above activity-log). */}
        {showAccountControls && (
          <Link
            to="/settings/team"
            className="flex items-center gap-3 w-full rounded-lg border border-base-200 bg-base-100 px-4 py-3 hover:bg-base-200 transition-colors sm:self-end min-h-[44px]"
            data-testid="team-link"
          >
            <UserCog className="w-5 h-5 text-base-content/60 shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="block font-medium text-sm leading-tight">{t('team')}</span>
              <span className="block text-xs text-base-content/60 leading-tight">{t('team_description')}</span>
            </div>
            <ChevronRight className="w-4 h-4 text-base-content/40 shrink-0" />
          </Link>
        )}

        {/* Activity Log quick-link — admin/owner only (F-011) */}
        {showAccountControls && (
          <Link
            to="/settings/activity-log"
            className="flex items-center gap-3 w-full rounded-lg border border-base-200 bg-base-100 px-4 py-3 hover:bg-base-200 transition-colors sm:self-end min-h-[44px]"
            data-testid="activity-log-link"
          >
            <Bell className="w-5 h-5 text-base-content/60 shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="block font-medium text-sm leading-tight">{t('activity_log_link.title')}</span>
              <span className="block text-xs text-base-content/60 leading-tight">{t('activity_log_link.subtitle')}</span>
            </div>
            <ChevronRight className="w-4 h-4 text-base-content/40 shrink-0" />
          </Link>
        )}

        {/* Tabs — single source of truth for both desktop tab bar (hidden sm:flex)
            and the mobile <Select> (sm:hidden). The shared <Tabs mobileSelect>
            component renders both from one options array.
            The section_label aria-label preserves the existing mobile Select
            label so screen readers and tests can locate the control. */}
        <Tabs
          value={activeTab}
          onChange={(v) => setActiveTab(v as SettingsTab)}
          aria-label={t('section_label')}
          options={[
            { value: 'account', label: t('account.title') },
            { value: 'payments', label: t('payment_methods.title') },
            { value: 'carriers', label: t('parcel_carriers.title') },
            { value: 'categories', label: t('maintenance_categories.title') },
            ...(canEdit ? [{ value: 'expense_categories' as SettingsTab, label: t('expense_categories.title') }] : []),
            { value: 'sources', label: t('customer_sources.title') },
            { value: 'liff', label: t('liff.tab_label') },
            // EP-157 T157-005: owner/admin only (decision 2), shown to every
            // account (decision 5) — hidden (not disabled) for staff/accountant.
            ...(showAppIntegration ? [{ value: 'app_integration' as SettingsTab, label: t('app_integration.tab_label') }] : []),
            // EP-99: dual-gate — show only when the win_pickup flag AND win_pickup_enabled.
            ...(showWinPickup ? [{ value: 'win_pickup' as SettingsTab, label: t('win_pickup.tab_label') }] : []),
            // EP-144 (Q-9): owner-only, deliberately NOT canEdit (which also admits admin).
            ...(showSubscription ? [{ value: 'subscription' as SettingsTab, label: t('subscription.tab_label') }] : []),
            // EP-160 S1: FlowRent help, then personal data requests. Owner and
            // admin (an admin reads FlowRent help, it never grants), both
            // shown in the iOS app too (account controls, not the
            // business-model surface EP-150 hides).
            ...(showAccountControls ? [{ value: 'support_access' as SettingsTab, label: t('support_access.tab_label') }] : []),
            ...(showAccountControls ? [{ value: 'data_requests' as SettingsTab, label: t('data_requests.tab_label') }] : []),
            // EP-150 (App Review 5.1.1v): always the last tab (owner decision
            // 2026-09-29, moved here from the user menu). An owner deletes the
            // whole account, anyone else only their own login; the server
            // decides the same way from the role.
            ...(supportSession ? [] : [{ value: 'delete_account' as SettingsTab, label: deleteTabLabel }]),
          ]}
        />

        {/* Account Tab */}
        {activeTab === 'account' && (
          <>
            {/* PAY-1 (T149-083): persistent inline alert, shown instead of a
                success toast whenever the save did not actually happen. */}
            {accountForm.saveError && (
              <SettingsSaveAlert error={accountForm.saveError} className="mb-6" />
            )}
            <form onSubmit={handleSaveAccount} noValidate>
            <Card>
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  id="name"
                  label={t('account.name')}
                  disabled={!canEdit}
                  required
                  error={errors.name?.message}
                  {...register('name', { required: true })}
                />
                <Select
                  label={t('account.language')}
                  options={languageOptions(t)}
                  disabled={!canEdit}
                  {...register('language')}
                />
                <Input
                  label={t('account.invoice_prefix')}
                  placeholder={t('account.invoice_prefix_placeholder')}
                  disabled={!canEdit}
                  {...register('invoice_prefix')}
                />
                <Select
                  label={t('account.timezone')}
                  options={timezoneOptions}
                  disabled={!canEdit}
                  {...register('timezone')}
                />
                <Select
                  label={t('account.date_format')}
                  options={dateFormatOptions}
                  disabled={!canEdit}
                  {...register('date_format')}
                />
                <Select
                  label={t('account.calendar')}
                  options={calendarOptions}
                  disabled={!canEdit}
                  {...register('calendar')}
                />
                <Select
                  label={t('account.currency')}
                  options={currencyOptions}
                  disabled={!canEdit}
                  {...register('currency')}
                />
              </div>
            </Card>

            {/* EP-160 (section 2.7, S1): the data controller the tenants'
                privacy notice names. A blank field means the server's default
                (account name, owner email), shown as the placeholder. Saved
                with the rest of this form. */}
            <Card className="mt-6" data-testid="privacy-controller-card">
              <h3 className="font-semibold mb-1">{t('account.privacy_controller.title')}</h3>
              <p className="text-sm text-base-content/60 mb-4">{t('account.privacy_controller.help')}</p>
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  id="controller_name"
                  className="min-w-0"
                  label={t('account.privacy_controller.name')}
                  placeholder={accountSettings?.controller_name_defaulted ? accountSettings.controller_name : undefined}
                  maxLength={CONTROLLER_FIELD_MAX}
                  disabled={!canEdit}
                  {...register('controller_name')}
                />
                <Input
                  id="controller_contact"
                  className="min-w-0"
                  label={t('account.privacy_controller.contact')}
                  placeholder={accountSettings?.controller_contact_defaulted ? accountSettings.controller_contact : undefined}
                  maxLength={CONTROLLER_FIELD_MAX}
                  disabled={!canEdit}
                  {...register('controller_contact')}
                />
              </div>
            </Card>

            {/* EP-111 T111-6 — condominium_enabled module toggle (single-gate,
                account-setting only). Downstream tasks (property-type dropdown,
                CAF sidebar, Co-owner sidebar) all read this via
                accountSettings.settings.condominium_enabled === true. */}
            <Card className="mt-6">
              <h3 className="font-semibold mb-3">{t('property_types.title')}</h3>
              <Toggle
                variant="primary"
                label={t('property_types.condominium_enabled_label')}
                disabled={!canEdit}
                {...register('condominium_enabled')}
              />
              <p className="text-sm text-base-content/60 mt-2">{t('property_types.condominium_enabled_description')}</p>
            </Card>

            {/* EP-136 (T136-026) — account-wide fallback for the 3 booking
                settings. Any property can override each key individually on
                its own Booking block (properties/:id → Overview tab); these
                three are NOT tri-state here, since there is no settings layer
                above the account itself. */}
            <Card className="mt-6">
              <h3 className="font-semibold mb-1">{t('booking.title')}</h3>
              <p className="text-sm text-base-content/60 mb-4">{t('booking.description')}</p>
              <div className="grid gap-4 md:grid-cols-2 [&>*]:min-w-0">
                <Select
                  label={t('booking.fee_mode_label')}
                  disabled={!canEdit}
                  options={[
                    { value: 'deposit', label: t('booking.fee_mode_options.deposit') },
                    { value: 'revenue', label: t('booking.fee_mode_options.revenue') },
                  ]}
                  {...register('booking_fee_mode')}
                />
                <Input
                  label={t('booking.fee_default_label')}
                  type="number"
                  min={0}
                  step="0.01"
                  disabled={!canEdit}
                  leftIcon={<span className="text-base-content/60">฿</span>}
                  hint={t('booking.fee_default_hint')}
                  {...register('booking_fee_default', { valueAsNumber: true })}
                />
              </div>
              <div className="mt-4 pt-4 border-t border-base-200">
                <Toggle
                  variant="primary"
                  label={t('booking.forfeit_label')}
                  disabled={!canEdit}
                  {...register('booking_fee_forfeit_on_cancel')}
                />
              </div>
            </Card>

            <Card className="mt-6">
              <h3 className="font-semibold mb-1">{t('tax_invoice.title')}</h3>
              <p className="text-sm text-base-content/60 mb-4">{t('tax_invoice.description')}</p>
              <div className="grid gap-4 md:grid-cols-2">
                {(() => {
                  const taxIdReg = register('tax_id')
                  return (
                    <Input
                      label={t('tax_invoice.tax_id')}
                      placeholder={t('tax_invoice.tax_id_placeholder')}
                      maxLength={13}
                      disabled={!canEdit}
                      error={errors.tax_id?.message}
                      {...taxIdReg}
                      ref={(el) => {
                        if (typeof taxIdReg.ref === 'function') taxIdReg.ref(el)
                        taxIdInputRef.current = el
                      }}
                    />
                  )
                })()}
                <Input
                  label={t('tax_invoice.branch_number')}
                  placeholder={t('tax_invoice.branch_number_placeholder')}
                  disabled={!canEdit}
                  error={errors.branch_number?.message}
                  {...register('branch_number')}
                />
              </div>
              <div className="mt-4">
                <Textarea
                  label={t('tax_invoice.address')}
                  placeholder={t('tax_invoice.address_placeholder')}
                  rows={3}
                  disabled={!canEdit}
                  {...register('tax_address')}
                />
              </div>
              <div className="mt-4 pt-4 border-t border-base-200">
                <Toggle
                  variant="primary"
                  label={t('tax_invoice.vat_enabled')}
                  disabled={!canEdit}
                  {...register('vat_enabled')}
                />
                {accountForm.form.watch('vat_enabled') && (
                  <div className="mt-3 max-w-xs">
                    <Input
                      label={t('tax_invoice.vat_rate')}
                      type="number"
                      min={0}
                      max={100}
                      step={0.1}
                      disabled={!canEdit}
                      error={errors.vat_rate?.message}
                      {...register('vat_rate', { valueAsNumber: true })}
                    />
                  </div>
                )}
              </div>
            </Card>

            {/* EP-53: 8 feature toggles (cmms, accounting, late_fees, announcements,
                 debt_collection, lpr, lead_generation, microsite) were moved from
                 this page to the admin-cli web UI (admin-cli/web/src/components/
                 SettingsToggles.tsx, reachable via AccountFormPage edit flow).
                 Customer cannot self-serve any of the 8 anymore — contact support
                 to enable/disable. The customer API permit list also dropped them
                 (account_settings_controller.rb line 104+) so a hand-crafted
                 PATCH carrying these keys is silently ignored. */}


            {/* EP-73 — AI feature coach (dual-gated; the Unleash flag is the
                ops half, this is the per-account customer toggle). */}
            {featureFlags.aiCoach && (
              <Card className="mt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold mb-1">{t('ai_coach.title')}</h3>
                    <p className="text-sm text-base-content/60">{t('ai_coach.description')}</p>
                  </div>
                  <Toggle
                    variant="primary"
                    checked={aiCoachEnabled}
                    disabled={!canEdit}
                    onChange={toggleCoach}
                    aria-label={t('ai_coach.title')}
                  />
                </div>

                {aiCoachEnabled && canEdit && (
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div>
                      <Input
                        type="number"
                        min={1}
                        label={t('ai_coach.max_features')}
                        value={coachMaxFeatures}
                        onChange={(e) => setCoachMaxFeatures(e.target.value)}
                        onBlur={saveCoachMaxFeatures}
                      />
                      <p className="text-xs text-base-content/60 mt-1">{t('ai_coach.max_features_hint')}</p>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {featureFlags.leadGeneration && (
              <Card className="mt-6">
                <h3 className="font-semibold mb-1">{t('lead_generation.title')}</h3>
                <p className="text-sm text-base-content/60 mb-4">{t('lead_generation.description')}</p>
                <div className="max-w-md">
                  <p className="text-sm">
                    {accountSettings?.settings?.line_oa?.display_name ? (
                      <>
                        <span className="text-success">●</span>{' '}
                        {t('lead_generation.line_oa_status_connected', {
                          name: accountSettings.settings.line_oa.display_name,
                          basicId: accountSettings.settings.line_oa.basic_id,
                        })}
                      </>
                    ) : (
                      <>
                        <span className="text-base-content/40">●</span> {t('lead_generation.line_oa_status_not_connected')}
                      </>
                    )}
                  </p>
                  <p className="text-sm text-base-content/60 mt-1">{t('lead_generation.line_oa_shared_hint')}</p>
                  <Link to="/settings?tab=liff" className="link link-primary text-sm">
                    {t('lead_generation.go_to_line_settings')}
                  </Link>
                </div>
              </Card>
            )}

            {/* Replay Onboarding — admin/owner only */}
            {canEdit && (
              <Card className="mt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold mb-1">{t('replay_onboarding.button')}</h3>
                    <p className="text-sm text-base-content/60">{t('replay_onboarding.confirm_body')}</p>
                  </div>
                  <Button
                    variant="outline"
                    leftIcon={<RotateCw className="w-4 h-4" />}
                    onClick={() => setReplayConfirmOpen(true)}
                    loading={replayOnboarding.isPending}
                    type="button"
                  >
                    {t('replay_onboarding.button')}
                  </Button>
                </div>
              </Card>
            )}

            {canEdit && (
              <SettingsSaveBar className="mt-6">
                <Button type="submit" loading={accountForm.isPending}>
                  {t('actions.save_settings')}
                </Button>
              </SettingsSaveBar>
            )}
            </form>
          </>
        )}

        {/* Payment Methods Tab */}
        {activeTab === 'payments' && (
          <>
            {/* PAY-1 (T149-082): persistent inline alert -- shown instead of a
                success toast whenever the save did not actually happen. */}
            {paymentSettings.saveError && (
              <SettingsSaveAlert error={paymentSettings.saveError} className="mb-6" />
            )}
            <form onSubmit={handleSavePayments}>
            <div className="space-y-8">
              {/* ── Basic ──────────────────────────────────────────────
                  Bank transfer, static QR image (invoice_qr_image), and the
                  native dynamic PromptPay Bill-Payment QR (biller_id). */}
              <div>
                <h3 className="text-sm font-semibold text-base-content/50 uppercase tracking-wider mb-3">
                  {t('payment_methods.groups.basic')}
                </h3>
                <div className="grid gap-6 lg:grid-cols-2">
                  {/* Bank Transfer */}
                  <Card>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-semibold">{t('payment_methods.bank_transfer.title')}</h3>
                        <p className="text-sm text-base-content/60">{t('payment_methods.bank_transfer.description')}</p>
                      </div>
                      <Toggle
                        variant="primary"
                        checked={paymentSettings.paymentMethods.bank_transfer?.enabled ?? false}
                        onChange={(e) => paymentSettings.updateBankTransfer('enabled', e.target.checked)}
                        disabled={!canEdit}
                      />
                    </div>
                    {paymentSettings.paymentMethods.bank_transfer?.enabled && (
                      <div className="space-y-3 pt-3 border-t border-base-200">
                        <Input
                          id="bank_name"
                          label={t('payment_methods.bank_transfer.bank_name')}
                          value={paymentSettings.paymentMethods.bank_transfer.bank_name ?? ''}
                          onChange={(e) => paymentSettings.updateBankTransfer('bank_name', e.target.value)}
                          disabled={!canEdit}
                          placeholder="Bangkok Bank"
                          error={paymentSettings.errors.bank_name}
                        />
                        <Input
                          id="account_number"
                          label={t('payment_methods.bank_transfer.account_number')}
                          value={paymentSettings.paymentMethods.bank_transfer.account_number ?? ''}
                          onChange={(e) => paymentSettings.updateBankTransfer('account_number', e.target.value)}
                          disabled={!canEdit}
                          placeholder="123-4-56789-0"
                          error={paymentSettings.errors.account_number}
                        />
                        <Input
                          id="account_name"
                          label={t('payment_methods.bank_transfer.account_name')}
                          value={paymentSettings.paymentMethods.bank_transfer.account_name ?? ''}
                          onChange={(e) => paymentSettings.updateBankTransfer('account_name', e.target.value)}
                          disabled={!canEdit}
                          placeholder="Peerapat Residence"
                          error={paymentSettings.errors.account_name}
                        />
                      </div>
                    )}
                  </Card>

                  {/* QR code (static image) — uploaded invoice_qr_image */}
                  <Card>
                    <h3 className="font-semibold mb-1">{t('payment_methods.invoice_qr.title')}</h3>
                    <p className="text-sm text-base-content/60 mb-4">{t('payment_methods.invoice_qr.description')}</p>
                    {invoiceQrKey && brokenQrKey !== invoiceQrKey && (
                      <div className="mb-3">
                        <img
                          src={resolveApiUrl(`/api/images/${invoiceQrKey}`)}
                          alt={t('payment_methods.invoice_qr.title')}
                          className="w-32 h-32 object-contain border rounded"
                          onError={() => setBrokenQrKey(invoiceQrKey)}
                        />
                      </div>
                    )}
                    {/* The saved key can outlive its file (uploads lost with the
                        old storage bucket, 2026-08-18). Say so instead of drawing
                        a broken image; a new upload gets a new key and shows. */}
                    {invoiceQrKey && brokenQrKey === invoiceQrKey && (
                      <Alert variant="warning" className="mb-3" data-testid="invoice-qr-missing">
                        {t('payment_methods.invoice_qr.missing')}
                      </Alert>
                    )}
                    {canEdit && (
                      <FileInput
                        accept="image/*"
                        size="sm"
                        className="max-w-xs"
                        onChange={async (e) => {
                          const file = e.target.files?.[0]
                          if (!file) return
                          try {
                            const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
                              filename: file.name, content_type: file.type,
                            })
                            // Save the key only once the file is really stored.
                            // fetch() does not reject on an error status, so an
                            // unchecked PUT could save a key with no file behind it.
                            const upload = await fetch(presigned.url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } })
                            if (!upload.ok) {
                              addToast({ type: 'error', message: t('payment_methods.invoice_qr.upload_failed') })
                              return
                            }
                            await updateAccountSettings.mutateAsync({
                              settings: { invoice_qr_image: presigned.key },
                            })
                            showSuccess()
                          } catch {
                            addToast({ type: 'error', message: t('payment_methods.invoice_qr.upload_failed') })
                          }
                        }}
                      />
                    )}
                  </Card>

                  {/* QR code (dynamic) — native PromptPay Bill-Payment (biller_id).
                      Extracted from the Stripe PromptPay card: it is our own EMVCo
                      QR, not Stripe-backed, so it belongs under Basic. */}
                  <Card>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-semibold">{t('payment_methods.promptpay_bill_payment.title')}</h3>
                        <p className="text-sm text-base-content/60">{t('payment_methods.promptpay_bill_payment.description')}</p>
                      </div>
                      <Toggle
                        variant="primary"
                        checked={paymentSettings.paymentMethods.promptpay_bill_payment?.enabled ?? false}
                        onChange={(e) => paymentSettings.updatePromptpayBillPayment('enabled', e.target.checked)}
                        disabled={!canEdit}
                        aria-label={t('payment_methods.promptpay_bill_payment.enabled_label')}
                      />
                    </div>
                    {paymentSettings.paymentMethods.promptpay_bill_payment?.enabled && (
                      <div className="space-y-3 pt-3 border-t border-base-200">
                        <Input
                          label={t('payment_methods.promptpay_bill_payment.biller_id_label')}
                          value={paymentSettings.paymentMethods.promptpay_bill_payment.biller_id ?? ''}
                          onChange={(e) => paymentSettings.updatePromptpayBillPayment('biller_id', e.target.value)}
                          disabled={!canEdit}
                          hint={t('payment_methods.promptpay_bill_payment.biller_id_helper')}
                        />
                        <Select
                          label={t('payment_methods.promptpay_bill_payment.ref2_mode_label')}
                          value={paymentSettings.paymentMethods.promptpay_bill_payment.ref2_mode ?? 'none'}
                          onChange={(e) => paymentSettings.updatePromptpayBillPayment('ref2_mode', e.target.value)}
                          disabled={!canEdit}
                          options={[
                            { value: 'plate', label: t('payment_methods.promptpay_bill_payment.ref2_mode_plate') },
                            { value: 'none', label: t('payment_methods.promptpay_bill_payment.ref2_mode_none') },
                          ]}
                        />
                        <Input
                          label={t('payment_methods.promptpay_bill_payment.proxy_target_label')}
                          value={paymentSettings.paymentMethods.promptpay_bill_payment.promptpay_proxy_target ?? ''}
                          onChange={(e) => paymentSettings.updatePromptpayBillPayment('promptpay_proxy_target', e.target.value)}
                          disabled={!canEdit}
                          hint={t('payment_methods.promptpay_bill_payment.proxy_target_helper')}
                        />
                      </div>
                    )}
                  </Card>

                  {/* Cash */}
                  <Card>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-semibold">{t('payment_methods.cash.title')}</h3>
                        <p className="text-sm text-base-content/60">{t('payment_methods.cash.description')}</p>
                      </div>
                      <Toggle
                        variant="primary"
                        checked={paymentSettings.paymentMethods.cash?.enabled ?? false}
                        onChange={(e) => paymentSettings.updateCash('enabled', e.target.checked)}
                        disabled={!canEdit}
                      />
                    </div>
                  </Card>
                </div>
              </div>

              {/* ── Stripe ─────────────────────────────────────────────
                  Stripe-backed PromptPay QR + credit card. */}
              <div>
                <h3 className="text-sm font-semibold text-base-content/50 uppercase tracking-wider mb-3">
                  {t('payment_methods.groups.stripe')}
                </h3>
                <div className="grid gap-6 lg:grid-cols-2">
                  {/* PromptPay (Stripe QR) */}
                  <Card>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-semibold">{t('payment_methods.promptpay.title')}</h3>
                        <p className="text-sm text-base-content/60">{t('payment_methods.promptpay.description')}</p>
                      </div>
                      <Toggle
                        variant="primary"
                        checked={paymentSettings.paymentMethods.promptpay?.enabled ?? false}
                        onChange={(e) => paymentSettings.updatePromptpay('enabled', e.target.checked)}
                        disabled={!canEdit}
                      />
                    </div>
                    {paymentSettings.paymentMethods.promptpay?.enabled && (
                      <div className="space-y-3 pt-3 border-t border-base-200">
                        <Alert variant="info">{t('payment_methods.promptpay.stripe_notice')}</Alert>
                        <Input
                          id="promptpay_pk"
                          label={t('payment_methods.promptpay.publishable_key')}
                          value={paymentSettings.paymentMethods.promptpay.stripe_publishable_key ?? ''}
                          onChange={(e) => paymentSettings.updatePromptpay('stripe_publishable_key', e.target.value)}
                          disabled={!canEdit}
                          placeholder="pk_live_..."
                          error={paymentSettings.errors.promptpay_pk}
                        />
                        <Input
                          id="promptpay_sk"
                          label={t('payment_methods.promptpay.secret_key')}
                          type="password"
                          value={paymentSettings.paymentMethods.promptpay.stripe_secret_key ?? ''}
                          onChange={(e) => paymentSettings.updatePromptpay('stripe_secret_key', e.target.value)}
                          disabled={!canEdit}
                          placeholder="sk_live_..."
                          error={paymentSettings.errors.promptpay_sk}
                        />
                        <Input
                          id="promptpay_wh"
                          label={t('payment_methods.promptpay.webhook_secret')}
                          type="password"
                          value={paymentSettings.paymentMethods.promptpay.stripe_webhook_secret ?? ''}
                          onChange={(e) => paymentSettings.updatePromptpay('stripe_webhook_secret', e.target.value)}
                          disabled={!canEdit}
                          placeholder="whsec_..."
                          error={paymentSettings.errors.promptpay_wh}
                        />
                      </div>
                    )}
                  </Card>

                  {/* Credit Card */}
                  <Card>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-semibold">{t('payment_methods.credit_card.title')}</h3>
                        <p className="text-sm text-base-content/60">{t('payment_methods.credit_card.description')}</p>
                      </div>
                      <Toggle
                        variant="primary"
                        checked={paymentSettings.paymentMethods.credit_card?.enabled ?? false}
                        onChange={(e) => paymentSettings.updateCreditCard('enabled', e.target.checked)}
                        disabled={!canEdit}
                      />
                    </div>
                    {paymentSettings.paymentMethods.credit_card?.enabled && (
                      <div className="space-y-3 pt-3 border-t border-base-200">
                        <Alert variant="info">{t('payment_methods.credit_card.stripe_notice')}</Alert>
                        <Input
                          id="credit_card_pk"
                          label={t('payment_methods.credit_card.publishable_key')}
                          value={paymentSettings.paymentMethods.credit_card.stripe_publishable_key ?? ''}
                          onChange={(e) => paymentSettings.updateCreditCard('stripe_publishable_key', e.target.value)}
                          disabled={!canEdit}
                          placeholder="pk_live_..."
                          error={paymentSettings.errors.credit_card_pk}
                        />
                        <Input
                          id="credit_card_sk"
                          label={t('payment_methods.credit_card.secret_key')}
                          type="password"
                          value={paymentSettings.paymentMethods.credit_card.stripe_secret_key ?? ''}
                          onChange={(e) => paymentSettings.updateCreditCard('stripe_secret_key', e.target.value)}
                          disabled={!canEdit}
                          placeholder="sk_live_..."
                          error={paymentSettings.errors.credit_card_sk}
                        />
                        <Input
                          id="credit_card_wh"
                          label={t('payment_methods.credit_card.webhook_secret')}
                          type="password"
                          value={paymentSettings.paymentMethods.credit_card.stripe_webhook_secret ?? ''}
                          onChange={(e) => paymentSettings.updateCreditCard('stripe_webhook_secret', e.target.value)}
                          disabled={!canEdit}
                          placeholder="whsec_..."
                          error={paymentSettings.errors.credit_card_wh}
                        />
                      </div>
                    )}
                  </Card>
                </div>
              </div>

              {/* ── EP-143 T143-010 — Receiving Accounts + Auto-verify Cap ───
                  The G4 recipient-match gate in SlipGateLadder consults this
                  list.  Never serialised to LIFF or kiosk endpoints. */}
              <div>
                <h3 className="text-sm font-semibold text-base-content/50 uppercase tracking-wider mb-3">
                  {t('payment_methods.receiving_accounts.title')}
                </h3>
                <Card>
                  {/* Receiving accounts list */}
                  {(paymentSettings.paymentMethods.receiving_accounts ?? []).length === 0 ? (
                    <p className="text-sm text-base-content/60 mb-4">
                      {t('payment_methods.receiving_accounts.empty')}
                    </p>
                  ) : (
                    <div className="space-y-3 mb-4">
                      {(paymentSettings.paymentMethods.receiving_accounts ?? []).map((ra, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-3 p-3 rounded-lg bg-base-200"
                          data-testid={`receiving-account-row-${idx}`}
                        >
                          <div className="flex-1 min-w-0 grid grid-cols-3 gap-2">
                            <Select
                              label={t('payment_methods.receiving_accounts.bank_code_label')}
                              value={ra.bank_code}
                              onChange={(e) =>
                                paymentSettings.updateReceivingAccount(idx, { ...ra, bank_code: e.target.value })
                              }
                              disabled={!canEdit}
                              options={[
                                { value: 'scb', label: 'SCB' },
                                { value: 'kbank', label: 'KBank' },
                                { value: 'ktb', label: 'KTB' },
                                { value: 'bbl', label: 'BBL' },
                                { value: 'bay', label: 'Krungsri (BAY)' },
                                { value: 'ttb', label: 'TTB' },
                                { value: 'gsb', label: 'GSB' },
                                { value: 'kkp', label: 'KKP' },
                                { value: 'promptpay_generic', label: 'PromptPay (generic)' },
                              ]}
                            />
                            <Input
                              label={t('payment_methods.receiving_accounts.account_number_label')}
                              value={ra.account_number}
                              onChange={(e) =>
                                paymentSettings.updateReceivingAccount(idx, { ...ra, account_number: e.target.value })
                              }
                              disabled={!canEdit}
                              // Display masked: show last 4 digits only when not focused
                              placeholder="012-3-45678-9"
                            />
                            <Input
                              label={t('payment_methods.receiving_accounts.display_name_label')}
                              value={ra.display_name}
                              onChange={(e) =>
                                paymentSettings.updateReceivingAccount(idx, { ...ra, display_name: e.target.value })
                              }
                              disabled={!canEdit}
                              placeholder="e.g. Main Account"
                            />
                          </div>
                          {canEdit && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm btn-circle flex-shrink-0 mt-5"
                              onClick={() => paymentSettings.removeReceivingAccount(idx)}
                              aria-label={`Remove receiving account ${idx + 1}`}
                              data-testid={`remove-receiving-account-${idx}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {canEdit && (
                    <Button
                      type="button"
                      variant="outline"
                      leftIcon={<Plus className="w-4 h-4" />}
                      onClick={() =>
                        paymentSettings.addReceivingAccount({
                          bank_code: 'scb',
                          account_number: '',
                          display_name: '',
                        })
                      }
                      data-testid="add-receiving-account"
                    >
                      {t('payment_methods.receiving_accounts.add_button')}
                    </Button>
                  )}

                  {/* EP-161 §2.2: the auto-verify cap field is gone. A person
                      verifies every slip, so slip_auto_verify_max_amount no
                      longer decides anything; the setting itself stays. */}
                </Card>
              </div>

              {/* ── Coming soon ────────────────────────────────────────
                  Disabled provider placeholders — Alipay / PaySolution / Beam. */}
              <div>
                <h3 className="text-sm font-semibold text-base-content/50 uppercase tracking-wider mb-3">
                  {t('payment_methods.groups.coming_soon')}
                </h3>
                <div className="grid gap-6 lg:grid-cols-2">
                  {[
                    { key: 'alipay', title: t('payment_methods.alipay.title'), description: t('payment_methods.alipay.description') },
                    { key: 'paysolution', title: t('payment_methods.paysolution.title'), description: t('payment_methods.paysolution.description') },
                    { key: 'beam', title: t('payment_methods.beam.title'), description: t('payment_methods.beam.description') },
                  ].map((provider) => (
                    <Card key={provider.key} className="opacity-50">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-semibold">{provider.title}</h3>
                          <p className="text-sm text-base-content/60">{provider.description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="ghost">{t('payment_methods.coming_soon')}</Badge>
                          <Toggle disabled aria-label={provider.title} />
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>

            {canEdit && (
              <SettingsSaveBar className="mt-6">
                <Button type="submit" loading={paymentSettings.isPending}>
                  {t('actions.save_settings')}
                </Button>
              </SettingsSaveBar>
            )}
            </form>
          </>
        )}

        {/* LIFF Tab */}
        {activeTab === 'liff' && (
          <>
            {liffSettings.saveError && (
              <SettingsSaveAlert error={liffSettings.saveError} className="mb-6" />
            )}
            <form onSubmit={handleSaveLiff}>
            <div className="space-y-6">
              {/* EP-156 T156-007 — one "LINE Official Account" card holds the
                  tenant OA's Channel Secret, Channel Access Token, LIFF ID
                  and LINE Login Channel ID, plus Test connection. The Save
                  Settings bar below saves it together with the theme
                  (handleSaveLiff). */}
              <LineOaSettingsCard ref={lineOaCardRef} canEdit={canEdit} />

              {/* App colours and the home banner, apart from the login page's
                  controls (2026-10-01, owner: on desktop one two-column grid mixed
                  the two and left holes beside the tall uploads). */}
              <Card>
                <h3 className="font-semibold mb-4">{t('liff.theme_settings')}</h3>
                <div className="grid gap-6 md:grid-cols-3">
                  {/* Primary Color */}
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">{t('liff.primary_color')}</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={liffSettings.liffTheme.primary_color}
                        onChange={(e) => liffSettings.updateThemeField('primary_color', e.target.value)}
                        disabled={!canEdit}
                        className="w-10 h-10 rounded cursor-pointer border border-base-300 p-0.5"
                        aria-label={t('liff.primary_color')}
                      />
                      <Input
                        type="text"
                        value={liffSettings.liffTheme.primary_color}
                        onChange={(e) => liffSettings.updateThemeField('primary_color', e.target.value)}
                        disabled={!canEdit}
                        className="flex-1"
                        inputClassName="font-mono text-sm"
                        placeholder="#2d6b6b"
                        aria-label={`${t('liff.primary_color')} hex`}
                      />
                    </div>
                  </div>

                  {/* Button Color */}
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">{t('liff.button_color')}</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={liffSettings.liffTheme.button_color}
                        onChange={(e) => liffSettings.updateThemeField('button_color', e.target.value)}
                        disabled={!canEdit}
                        className="w-10 h-10 rounded cursor-pointer border border-base-300 p-0.5"
                        aria-label={t('liff.button_color')}
                      />
                      <Input
                        type="text"
                        value={liffSettings.liffTheme.button_color}
                        onChange={(e) => liffSettings.updateThemeField('button_color', e.target.value)}
                        disabled={!canEdit}
                        className="flex-1"
                        inputClassName="font-mono text-sm"
                        placeholder="#2d6b6b"
                        aria-label={`${t('liff.button_color')} hex`}
                      />
                    </div>
                  </div>

                  {/* Home Hero Color */}
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">{t('liff.home_hero_color')}</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={liffSettings.liffTheme.home_hero_color}
                        onChange={(e) => liffSettings.updateThemeField('home_hero_color', e.target.value)}
                        disabled={!canEdit}
                        className="w-10 h-10 rounded cursor-pointer border border-base-300 p-0.5"
                        aria-label={t('liff.home_hero_color')}
                      />
                      <Input
                        type="text"
                        value={liffSettings.liffTheme.home_hero_color}
                        onChange={(e) => liffSettings.updateThemeField('home_hero_color', e.target.value)}
                        disabled={!canEdit}
                        className="flex-1"
                        inputClassName="font-mono text-sm"
                        placeholder="#2d6b6b"
                        aria-label={`${t('liff.home_hero_color')} hex`}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-6 md:max-w-md">
                    {/* Home Hero Image */}
                    <div className="form-control">
                      <FileUpload
                        label={t('liff.home_hero_image')}
                        value={liffSettings.liffTheme.home_hero_image ? [liffSettings.liffTheme.home_hero_image] : []}
                        onChange={(urls) => liffSettings.updateThemeField('home_hero_image', urls[0] || '')}
                        maxFiles={1}
                        accept="image/*"
                        disabled={!canEdit}
                        onUpload={uploadThemeImage}
                      />
                    </div>
                </div>
              </Card>

              {/* The login page: its controls on the left, the live preview on
                  the right (sticky while the controls scroll). Stacked on a phone,
                  preview last. */}
              <Card>
                <h3 className="font-semibold mb-4">{t('liff.login_page_settings')}</h3>
                <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_auto]">
                  <div className="min-w-0 space-y-6">
                    <div className="grid gap-6 md:grid-cols-2">
                      {/* Login Background Color */}
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">{t('liff.login_bg_color')}</span>
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={liffSettings.liffTheme.login_bg_color}
                            onChange={(e) => liffSettings.updateThemeField('login_bg_color', e.target.value)}
                            disabled={!canEdit}
                            className="w-10 h-10 rounded cursor-pointer border border-base-300 p-0.5"
                            aria-label={t('liff.login_bg_color')}
                          />
                          <Input
                            type="text"
                            value={liffSettings.liffTheme.login_bg_color}
                            onChange={(e) => liffSettings.updateThemeField('login_bg_color', e.target.value)}
                            disabled={!canEdit}
                            className="flex-1"
                            inputClassName="font-mono text-sm"
                            placeholder="#ffffff"
                            aria-label={`${t('liff.login_bg_color')} hex`}
                          />
                        </div>
                      </div>

                      {/* Login title alignment (2026-09-30). Applies with or
                          without the panel, so it sits outside the panel block. */}
                      <Select
                        label={t('liff.login_title_align')}
                        value={liffSettings.liffTheme.login_title_align || 'left'}
                        onChange={(e) => liffSettings.updateThemeField('login_title_align', e.target.value)}
                        disabled={!canEdit}
                        options={LOGIN_TITLE_ALIGNS.map((align) => ({ value: align, label: t(`liff.login_title_align_${align}`) }))}
                      />
                    </div>

                    {/* Login Background Image */}
                    <div className="form-control">
                      <FileUpload
                        label={t('liff.login_bg_image')}
                        value={liffSettings.liffTheme.login_bg_image ? [liffSettings.liffTheme.login_bg_image] : []}
                        onChange={(urls) => liffSettings.updateThemeField('login_bg_image', urls[0] || '')}
                        maxFiles={1}
                        accept="image/*"
                        disabled={!canEdit}
                        onUpload={uploadThemeImage}
                      />
                    </div>

                    {/* Login Panel */}
                    <div className="form-control">
                      <label className="label cursor-pointer justify-start gap-3">
                        <Checkbox
                          size="sm"
                          checked={liffSettings.liffTheme.login_panel_enabled === 'true'}
                          onChange={(e) => liffSettings.updateThemeField('login_panel_enabled', e.target.checked ? 'true' : 'false')}
                          disabled={!canEdit}
                        />
                        <span className="label-text">{t('liff.login_panel_enabled')}</span>
                      </label>
                    </div>

                    {liffSettings.liffTheme.login_panel_enabled === 'true' && (
                      <>
                        <div className="grid gap-6 md:grid-cols-2">
                          <div className="form-control">
                            <label className="label">
                              <span className="label-text">{t('liff.login_panel_color')}</span>
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={liffSettings.liffTheme.login_panel_color}
                                onChange={(e) => liffSettings.updateThemeField('login_panel_color', e.target.value)}
                                disabled={!canEdit}
                                className="w-10 h-10 rounded cursor-pointer border border-base-300 p-0.5"
                                aria-label={t('liff.login_panel_color')}
                              />
                              <Input
                                type="text"
                                value={liffSettings.liffTheme.login_panel_color}
                                onChange={(e) => liffSettings.updateThemeField('login_panel_color', e.target.value)}
                                disabled={!canEdit}
                                className="flex-1"
                                inputClassName="font-mono text-sm"
                                placeholder="#ffffff"
                              />
                            </div>
                          </div>

                          <div className="form-control">
                            <label className="label">
                              <span className="label-text">{t('liff.login_panel_opacity')} ({Math.round(Number(liffSettings.liffTheme.login_panel_opacity) * 100)}%)</span>
                            </label>
                            {/* The colour field's height, so the slider lines up with it. */}
                            <div className="flex h-10 items-center">
                              <input
                                type="range"
                                min="0.1"
                                max="1"
                                step="0.05"
                                value={liffSettings.liffTheme.login_panel_opacity}
                                onChange={(e) => liffSettings.updateThemeField('login_panel_opacity', e.target.value)}
                                disabled={!canEdit}
                                className="range range-sm w-full"
                                aria-label={t('liff.login_panel_opacity')}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Panel margin (2026-10-01), padding and corner radius
                            (2026-09-30). Empty keeps the original look (12px from
                            the screen edge, 12px sideways and 16px above and
                            below past the form, 16px corners). */}
                        <div className="grid gap-4 sm:grid-cols-3">
                          <Input
                            type="number"
                            min={0}
                            max={LOGIN_PANEL_MARGIN_MAX_PX}
                            step={1}
                            label={t('liff.login_panel_margin')}
                            value={liffSettings.liffTheme.login_panel_margin}
                            onChange={(e) => liffSettings.updateThemeField('login_panel_margin', e.target.value)}
                            disabled={!canEdit}
                            placeholder="12"
                            data-testid="liff-login-panel-margin"
                          />
                          <Input
                            type="number"
                            min={0}
                            max={LOGIN_PANEL_PADDING_MAX_PX}
                            step={1}
                            label={t('liff.login_panel_padding')}
                            value={liffSettings.liffTheme.login_panel_padding}
                            onChange={(e) => liffSettings.updateThemeField('login_panel_padding', e.target.value)}
                            disabled={!canEdit}
                            placeholder="12"
                            data-testid="liff-login-panel-padding"
                          />
                          <Input
                            type="number"
                            min={0}
                            max={LOGIN_PANEL_RADIUS_MAX_PX}
                            step={1}
                            label={t('liff.login_panel_radius')}
                            value={liffSettings.liffTheme.login_panel_radius}
                            onChange={(e) => liffSettings.updateThemeField('login_panel_radius', e.target.value)}
                            disabled={!canEdit}
                            placeholder="16"
                            data-testid="liff-login-panel-radius"
                          />
                        </div>
                      </>
                    )}
                  </div>

                  <div className="lg:sticky lg:top-6 lg:self-start">
                    {/* Login Preview: the LIFF lease-code page's own markup
                        (liff/src/pages/LeaseCodePage.tsx) at phone size, scaled
                        to half by the shared stylesheet, in Thai, the language a
                        tenant sees until they switch (2026-10-01, owner: the
                        hand-drawn preview differed from the page). Keep the
                        markup in step with that page. */}
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">{t('liff.login_preview')}</span>
                      </label>
                      <div className="liff-login-preview" aria-hidden="true">
                        <div className="liff-login-preview__stage">
                          <div
                            className="liff-login"
                            data-testid="liff-login-preview"
                            style={liffLoginPreviewVars(
                              liffSettings.liffTheme,
                              liffSettings.liffTheme.login_bg_image ? resolveApiUrl(liffSettings.liffTheme.login_bg_image) : undefined,
                            )}
                          >
                            <div className="liff-login__form-wrapper">
                              {liffSettings.liffTheme.login_panel_enabled === 'true' && (
                                <div className="liff-login__panel" />
                              )}
                              <div className="liff-login__content">
                                <h1 ref={previewTitleRef} className="liff-login__title text-base-content">{t('liff.login_preview_page.title', { lng: 'th' })}</h1>
                                <div className="liff-login__fields">
                                  <div className="form-control">
                                    <div className="flex gap-2">
                                      <input
                                        type="text"
                                        className="input input-bordered flex-1 min-w-0 uppercase tracking-widest text-lg"
                                        placeholder={t('liff.login_preview_page.placeholder', { lng: 'th' })}
                                        readOnly
                                        tabIndex={-1}
                                      />
                                      <span className="btn btn-outline btn-square shrink-0">
                                        <QrCode className="w-5 h-5" aria-hidden="true" />
                                      </span>
                                    </div>
                                  </div>
                                  <span className="btn w-full liff-login__btn-primary">
                                    {t('liff.login_preview_page.send_otp', { lng: 'th' })}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              <Card>
                <h3 className="font-semibold mb-4">{t('liff.font_settings')}</h3>
                <Input
                  label={t('liff.font_family')}
                  value={liffSettings.liffTheme.font_family}
                  onChange={(e) => liffSettings.updateThemeField('font_family', e.target.value)}
                  disabled={!canEdit}
                  placeholder="Bai Jamjuree"
                />
                <p className="text-xs text-base-content/60 mt-1">{t('liff.font_family_hint')}</p>
              </Card>

              {/* EP-73 C4 — LINE rich menu (owner-only: rewrites the OA surface
                  for every tenant). EP-156 T156-007: the channel access token
                  input moved to the LINE Official Account card above — this
                  card now only applies the menu image with the token already
                  saved there. */}
              {user?.role === 'owner' && (
                <Card>
                  <h3 className="font-semibold mb-4">{t('rich_menu.title')}</h3>
                  <div className="space-y-4">
                    <div>
                      <FileInput
                        label={t('rich_menu.image')}
                        accept="image/png,image/jpeg"
                        hint={t('rich_menu.image_hint')}
                        onChange={(e) => setRichMenuFile(e.target.files?.[0] ?? null)}
                      />
                    </div>
                    <Button
                      type="button"
                      disabled={!richMenuFile || isApplyingRichMenu}
                      loading={isApplyingRichMenu}
                      onClick={handleApplyRichMenu}
                    >
                      {t('rich_menu.apply')}
                    </Button>
                  </div>
                </Card>
              )}
            </div>

            {canEdit && (
              <SettingsSaveBar className="mt-6">
                <Button type="submit" loading={savingLiff}>
                  {t('actions.save_settings')}
                </Button>
              </SettingsSaveBar>
            )}
            </form>
          </>
        )}

        {/* App Integration Tab: EP-157 T157-005, owner/admin only */}
        {activeTab === 'app_integration' && showAppIntegration && (
          <AppIntegrationSettingsCard canEdit={canEdit} />
        )}

        {/* Carriers Tab */}
        {activeTab === 'carriers' && (
          <>
            {carrierSettings.saveError && (
              <SettingsSaveAlert error={carrierSettings.saveError} className="mb-6" />
            )}
            <Card>
              <h3 className="font-semibold mb-4">{t('parcel_carriers.carrier_list')}</h3>

              <div className="space-y-2 mb-4">
                {carrierSettings.carriers.map((carrier, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 bg-base-200/50 rounded-lg"
                  >
                    <GripVertical className="w-4 h-4 text-base-content/30" />
                    <span className="flex-1">{carrier}</span>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="xs"
                        shape="circle"
                        className="text-error"
                        onClick={() => carrierSettings.removeCarrier(index)}
                        aria-label={t('parcel_carriers.remove_carrier')}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {canEdit && (
                <div className="flex gap-2">
                  <Input
                    value={carrierSettings.newCarrier}
                    onChange={(e) => carrierSettings.setNewCarrier(e.target.value)}
                    placeholder={t('parcel_carriers.add_placeholder')}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), carrierSettings.addCarrier())}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={carrierSettings.addCarrier}
                    disabled={!carrierSettings.newCarrier.trim()}
                  >
                    {t('parcel_carriers.add_carrier')}
                  </Button>
                </div>
              )}
            </Card>

            {canEdit && (
              <SettingsSaveBar>
                <Button variant="ghost" onClick={carrierSettings.resetCarriers}>
                  {t('parcel_carriers.reset_defaults')}
                </Button>
                <Button onClick={handleSaveCarriers} loading={carrierSettings.isPending}>
                  {t('actions.save_settings')}
                </Button>
              </SettingsSaveBar>
            )}
          </>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <>
            {categorySettings.saveError && (
              <SettingsSaveAlert error={categorySettings.saveError} className="mb-6" />
            )}
            <Card>
              <h3 className="font-semibold mb-2">{t('maintenance_categories.title')}</h3>
              <p className="text-sm text-base-content/60 mb-4">{t('maintenance_categories.description')}</p>

              <div className="space-y-2 mb-4">
                {categorySettings.categories.map((cat, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 bg-base-200/50 rounded-lg"
                  >
                    <GripVertical className="w-4 h-4 text-base-content/30" />
                    <span className="flex-1">
                      {i18n.language === 'th' ? cat.th : cat.en}
                    </span>
                    <span className="text-xs text-base-content/40 ml-2">
                      {i18n.language === 'th' ? cat.en : cat.th}
                    </span>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="xs"
                        shape="circle"
                        className="text-error"
                        onClick={() => categorySettings.removeCategory(index)}
                        aria-label={t('maintenance_categories.remove_category')}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {canEdit && (
                <div className="flex gap-2">
                  <Input
                    value={categorySettings.newCatEn}
                    onChange={(e) => categorySettings.setNewCatEn(e.target.value)}
                    placeholder={t('maintenance_categories.add_placeholder_en')}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), categorySettings.addCategory())}
                    className="flex-1"
                  />
                  <Input
                    value={categorySettings.newCatTh}
                    onChange={(e) => categorySettings.setNewCatTh(e.target.value)}
                    placeholder={t('maintenance_categories.add_placeholder_th')}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), categorySettings.addCategory())}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={categorySettings.addCategory}
                    disabled={!categorySettings.newCatEn.trim()}
                  >
                    {t('maintenance_categories.add_category')}
                  </Button>
                </div>
              )}
            </Card>

            {canEdit && (
              <SettingsSaveBar>
                <Button variant="ghost" onClick={categorySettings.resetCategories}>
                  {t('maintenance_categories.reset_defaults')}
                </Button>
                <Button onClick={handleSaveCategories} loading={categorySettings.isPending}>
                  {t('actions.save_settings')}
                </Button>
              </SettingsSaveBar>
            )}
          </>
        )}

        {/* Expense Categories Tab */}
        {activeTab === 'expense_categories' && (
          <>
            {expenseCategorySettings.saveError && (
              <SettingsSaveAlert error={expenseCategorySettings.saveError} className="mb-6" />
            )}
            <Card>
              <h3 className="font-semibold mb-2">{t('expense_categories.title')}</h3>
              <p className="text-sm text-base-content/60 mb-4">{t('expense_categories.description')}</p>

              <div className="space-y-2 mb-4">
                {expenseCategorySettings.categories.map((cat, index) => (
                  <div
                    key={cat.key}
                    className="flex flex-wrap items-center gap-3 p-3 bg-base-200/50 rounded-lg min-w-0"
                  >
                    <GripVertical className="w-4 h-4 shrink-0 text-base-content/30" />
                    <span className="flex-1 min-w-0 break-words">
                      {i18n.language === 'th' ? cat.th : cat.en}
                      <span className="block text-xs text-base-content/40">
                        {i18n.language === 'th' ? cat.en : cat.th}
                      </span>
                    </span>
                    {canEdit && accountSettings?.settings.accounting_enabled && (
                      <div className="order-last w-full min-w-0 sm:order-none sm:w-auto">
                        <ExpenseCategoryGlSelect category={cat} onChange={(code) => expenseCategorySettings.setGlCode(index, code)} />
                      </div>
                    )}
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="xs"
                        shape="circle"
                        className="text-error"
                        onClick={() => expenseCategorySettings.removeCategory(index)}
                        aria-label={t('expense_categories.remove_category')}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {canEdit && (
                <div className="flex gap-2">
                  <Input
                    value={expenseCategorySettings.newCatEn}
                    onChange={(e) => expenseCategorySettings.setNewCatEn(e.target.value)}
                    placeholder={t('expense_categories.add_placeholder_en')}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), expenseCategorySettings.addCategory())}
                    className="flex-1"
                  />
                  <Input
                    value={expenseCategorySettings.newCatTh}
                    onChange={(e) => expenseCategorySettings.setNewCatTh(e.target.value)}
                    placeholder={t('expense_categories.add_placeholder_th')}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), expenseCategorySettings.addCategory())}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={expenseCategorySettings.addCategory}
                    disabled={!expenseCategorySettings.newCatEn.trim()}
                  >
                    {t('expense_categories.add_category')}
                  </Button>
                </div>
              )}
            </Card>

            {canEdit && (
              <SettingsSaveBar>
                <Button variant="ghost" onClick={expenseCategorySettings.resetCategories}>
                  {t('expense_categories.reset_defaults')}
                </Button>
                <Button onClick={handleSaveExpenseCategories} loading={expenseCategorySettings.isPending}>
                  {t('actions.save_settings')}
                </Button>
              </SettingsSaveBar>
            )}
          </>
        )}

        {/* Customer Sources Tab */}
        {activeTab === 'sources' && (
          <>
            {sourceSettings.saveError && (
              <SettingsSaveAlert error={sourceSettings.saveError} className="mb-6" />
            )}
            <Card>
              <h3 className="font-semibold mb-2">{t('customer_sources.title')}</h3>
              <p className="text-sm text-base-content/60 mb-4">{t('customer_sources.description')}</p>

              {sourceSettings.sources.length === 0 ? (
                <p className="text-sm text-base-content/40 py-2">{t('customer_sources.no_sources')}</p>
              ) : (
                <div className="space-y-2 mb-4">
                  {sourceSettings.sources.map((source, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-base-200/50 rounded-lg"
                    >
                      <GripVertical className="w-4 h-4 text-base-content/30" />
                      <span className="flex-1">
                        {i18n.language === 'th' ? source.th : source.en}
                      </span>
                      <span className="text-xs text-base-content/40 ml-2">
                        {i18n.language === 'th' ? source.en : source.th}
                      </span>
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="xs"
                          shape="circle"
                          className="text-error"
                          onClick={() => sourceSettings.removeSource(index)}
                          aria-label={t('customer_sources.remove_source')}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {canEdit && (
                <div className="flex gap-2">
                  <Input
                    value={sourceSettings.newSourceEn}
                    onChange={(e) => sourceSettings.setNewSourceEn(e.target.value)}
                    placeholder={t('customer_sources.add_placeholder_en')}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), sourceSettings.addSource())}
                    className="flex-1"
                  />
                  <Input
                    value={sourceSettings.newSourceTh}
                    onChange={(e) => sourceSettings.setNewSourceTh(e.target.value)}
                    placeholder={t('customer_sources.add_placeholder_th')}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), sourceSettings.addSource())}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={sourceSettings.addSource}
                    disabled={!sourceSettings.newSourceEn.trim()}
                  >
                    {t('customer_sources.add_source')}
                  </Button>
                </div>
              )}
            </Card>

            {canEdit && (
              <SettingsSaveBar>
                <Button variant="ghost" onClick={sourceSettings.resetSources}>
                  {t('customer_sources.reset_defaults')}
                </Button>
                <Button onClick={handleSaveSources} loading={sourceSettings.isPending}>
                  {t('actions.save_settings')}
                </Button>
              </SettingsSaveBar>
            )}
          </>
        )}

        {/* WIN Pickup Tab — EP-99, dual-gate: win_pickup flag AND win_pickup_enabled */}
        {activeTab === 'win_pickup' && showWinPickup && (
          <WinPickupSettingsSection canEdit={canEdit} />
        )}

        {/* Subscription Tab: EP-144, owner-only (Q-9), read-only (no Save) */}
        {activeTab === 'subscription' && showSubscription && <SubscriptionTab />}

        {/* FlowRent help: EP-160 §2.1, S2. The owner grants; an admin reads. */}
        {activeTab === 'support_access' && showAccountControls && (
          <SupportAccessCard canManage={user?.role === 'owner'} requestId={supportRequestId} />
        )}

        {/* Personal data requests: EP-160 §2.7, owner and admin */}
        {activeTab === 'data_requests' && showAccountControls && <DataSubjectRequestsCard />}

        {/* Delete account: EP-150, the deletion flow itself, not a link to it.
            EP-160 §2.7: the owner can first download all the account's data. */}
        {activeTab === 'delete_account' && !supportSession && (
          <div className="space-y-6">
            {user?.role === 'owner' && <AccountExportCard />}
            <AccountDeletionPanel />
          </div>
        )}
      </div>

      {/* Confirm dialog for Replay onboarding. Outside the form, to avoid a
          nested <form>, and outside the space-y column: its dialog element stays
          in the DOM while closed, and as a sibling after the Account form it gave
          that form a 24px bottom margin, which left a grey strip under the
          sticky save bar at the end of the tab. */}
      <ConfirmDialog
        isOpen={replayConfirmOpen}
        onClose={() => setReplayConfirmOpen(false)}
        onConfirm={() => replayOnboarding.mutate()}
        title={t('replay_onboarding.confirm_title')}
        message={t('replay_onboarding.confirm_body')}
        confirmLabel={t('replay_onboarding.confirm_yes')}
        variant="warning"
        loading={replayOnboarding.isPending}
      />
    </>
  )
}

// EP-160 §2.7 "Leaving with the data": the owner's "download all data" above
// the deletion panel. The server mails the link to the owner only; nothing is
// downloaded here.
function AccountExportCard() {
  const { t } = useTranslation('settings')
  const addToast = useNotificationStore((s) => s.addToast)
  const requestExport = useRequestAccountExport()

  const handleClick = () => {
    requestExport.mutate(undefined, {
      onSuccess: () => addToast({ type: 'success', message: t('delete_account.export_requested') }),
      onError: (err) => {
        const code = isAxiosError<{ error?: { code?: string } }>(err) ? err.response?.data?.error?.code : undefined
        if (code === 'owner_email_recently_changed') {
          addToast({ type: 'error', message: t('delete_account.export_owner_email_recently_changed') })
        } else if (code === 'export_in_progress') {
          addToast({ type: 'info', message: t('delete_account.export_in_progress') })
        } else {
          addToast({ type: 'error', message: i18n.t('common:errors.something_went_wrong') })
        }
      },
    })
  }

  return (
    <Card title={t('delete_account.export_title')} description={t('delete_account.export_body')}>
      <div>
        <Button
          variant="outline"
          leftIcon={<Download className="w-4 h-4" />}
          onClick={handleClick}
          loading={requestExport.isPending}
          data-testid="account-export-request"
        >
          {t('delete_account.export_button')}
        </Button>
      </div>
    </Card>
  )
}
